import { type ThemeName } from './tokens';
import { componentColors } from './components';

// Legacy class mappings for each theme
const legacyClassMappings = {
  light: {
    // Backgrounds
    'bg-transparent-light': undefined, // Keep default
    'dark:bg-transparent-dark': undefined, // Keep default
    'bg-gray-50': undefined,
    'dark:bg-gray-900': undefined,

    // Text
    'text-gray-600': undefined,
    'dark:text-gray-400': undefined,
    'text-gray-700': undefined,
    'dark:text-gray-300': undefined,

    // Borders
    'border-gray-200': undefined,
    'dark:border-gray-700': undefined,
    'border-gray-200/50': undefined,
    'dark:border-gray-700/50': undefined,
  },

  dark: {
    // Keep dark theme using Tailwind's dark mode
    'bg-transparent-light': undefined,
    'dark:bg-transparent-dark': undefined,
    'bg-gray-50': undefined,
    'dark:bg-gray-900': undefined,

    // Text colors stay the same
    'text-gray-600': undefined,
    'dark:text-gray-400': undefined,
    'text-gray-700': undefined,
    'dark:text-gray-300': undefined,

    // Borders stay the same
    'border-gray-200': undefined,
    'dark:border-gray-700': undefined,
    'border-gray-200/50': undefined,
    'dark:border-gray-700/50': undefined,
  },

  spotify: {
    // Override background colors
    'bg-transparent-light': 'bg-[#282828]',
    'dark:bg-transparent-dark': 'bg-[#181818]',
    'bg-gray-50': 'bg-[#121212]',
    'dark:bg-gray-900': 'bg-[#121212]',

    // Override text colors
    'text-gray-600': 'text-[#B3B3B3]',
    'dark:text-gray-400': 'text-[#B3B3B3]',
    'text-gray-700': 'text-white',
    'dark:text-gray-300': 'text-white',

    // Override borders
    'border-gray-200': 'border-[#282828]',
    'dark:border-gray-700': 'border-[#282828]',
    'border-gray-200/50': 'border-[#282828]/50',
    'dark:border-gray-700/50': 'border-[#282828]/50',
  },
} as const;

export type LegacyClassName = keyof typeof legacyClassMappings.light;

// Helper to combine classes
export const cn = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(' ');
};

export const createHybridTheme = (theme: ThemeName) => {
  // Get new theme colors
  const colors = componentColors[theme];

  // Get legacy class mappings
  const legacyMappings = legacyClassMappings[theme];

  return {
    // New theme system
    colors,

    // Legacy compatibility
    classNames: legacyMappings,

    // Helper to get class names with legacy fallback
    getClasses: (baseClasses: string) => {
      const classes = baseClasses.split(' ');
      const mappedClasses = classes.map(cls => {
        // If class has a mapping, use it, otherwise keep original
        return legacyMappings[cls as LegacyClassName] || cls;
      });
      return cn(...mappedClasses);
    },
  };
};

// Hook to use in components during migration
export const useHybridClasses = (baseClasses: string) => {
  // TODO: Get current theme from context
  const theme = 'light' as ThemeName;
  const hybridTheme = createHybridTheme(theme);
  return hybridTheme.getClasses(baseClasses);
};
